import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import SectorEnemyArt from './SectorEnemyArt';
import { getSectorMinion } from './sectorEnemies';
import { RELICS } from './relics';
import RelicIcon from './RelicIcon';
import RewardCollection, { RewardPotion } from './RewardCollection';
import { getRewardCollectionDuration } from './rewardCollectionTiming';
import BreachIgnition from './BreachIgnition';
import { playSound } from '../../utils/utils';
import './SectorEncounters.css';

const EncounterLootSequence = ({
  result,
  reduceMotion,
  onComplete,
  healthRef,
  shardsRef,
  onRewardProgress,
}) => {
  const root = useRef(null);
  const rewardOriginRef = useRef(null);
  const done = useRef(false);
  const ignitionPlayed = useRef(false);
  const completeRef = useRef(onComplete);
  completeRef.current = onComplete;
  const finish = () => {
    if (done.current) return;
    done.current = true;
    completeRef.current();
  };
  useEffect(() => {
    const previousFocus = document.activeElement;
    const release = lockBodyScroll();
    const siblings = Array.from(document.body.children)
      .filter((node) => !node.contains(root.current))
      .map((node) => [node, node.hasAttribute('inert')]);
    siblings.forEach(([node]) => node.setAttribute('inert', ''));
    root.current?.focus({ preventScroll: true });
    if (result.training && !ignitionPlayed.current) {
      ignitionPlayed.current = true;
      playSound('/sounds/futuristic-zoom.mp3', { preventOverlap: true });
    }
    const timer = reduceMotion
      ? null
      : setTimeout(() => {
          if (!done.current) {
            done.current = true;
            completeRef.current();
          }
        }, Math.max(result.training ? 6500 : 3600, 1150 + getRewardCollectionDuration(result, reduceMotion)));
    return () => {
      clearTimeout(timer);
      release();
      siblings.forEach(([node, wasInert]) => {
        if (!wasInert) node.removeAttribute('inert');
      });
      if (previousFocus?.isConnected)
        previousFocus.focus({ preventScroll: true });
    };
  }, [reduceMotion, result]);
  const enemy = result.enemy || getSectorMinion(result.floor);
  const loot = result.loot || {};
  const killed = result.success;
  const training = !!result.training;
  const remaining =
    result.enemyHpAfter ??
    Math.max(0, (result.target || 0) - Math.max(0, result.effectiveRoll || 0));
  const title = training
    ? 'The breach is yours'
    : killed
    ? result.miniBoss
      ? 'The Warden is broken'
      : 'Minion shattered'
    : result.miniBoss
    ? 'The Warden strikes back'
    : 'The minion survives';
  return createPortal(
    <div
      ref={root}
      className={
        'coreboundEncounterCurtain has-reward-hud ' +
        (killed ? 'is-kill' : 'is-retaliation') +
        (training ? ' is-breach-ignition' : '') +
        (reduceMotion ? ' is-reduced' : '')
      }
      style={{ '--enemy-color': enemy.color }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="coreboundEncounterTitle"
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          finish();
        }
        if (event.key === 'Tab') {
          event.preventDefault();
          root.current?.querySelector('button')?.focus();
        }
      }}
    >
      <div className="coreboundEncounterStars" aria-hidden="true" />
      <header>
        <span>RIFT LOOM / SECTOR {String(result.floor).padStart(2, '0')}</span>
        <h2 id="coreboundEncounterTitle">{title}</h2>
        <p>
          {training
            ? 'First contact. First victory. The galaxy is listening.'
            : enemy.name}
          {training
            ? ''
            : ' · ' +
              (result.miniBoss
                ? 'Round ' + result.round
                : 'One shot encounter')}
        </p>
      </header>
      <div
        className="coreboundEncounterStage"
        ref={rewardOriginRef}
        aria-hidden="true"
      >
        {training ? (
          <BreachIgnition />
        ) : result.guardian ? (
          <div className="coreboundGuardianLootEmblem">✦</div>
        ) : (
          <>
            <div className="coreboundEncounterRay" />
            {!killed && <div className="coreboundEncounterCounter" />}
            <div className="coreboundEncounterVictim">
              <SectorEnemyArt enemy={enemy} />
            </div>
            <div className="coreboundEncounterBurst">
              {Array.from({ length: 12 }, (_, index) => (
                <i
                  key={index}
                  style={{
                    '--fragment-angle': index * 30 + 'deg',
                    '--fragment-delay': (index % 3) * 0.04 + 's',
                  }}
                />
              ))}
            </div>
            <strong className="coreboundEncounterDamage">
              {training
                ? 'SIGNAL LINKED'
                : '−' +
                  Math.max(0, result.attackDealt ?? result.effectiveRoll ?? 0)}
            </strong>
          </>
        )}
      </div>
      <div className="coreboundEncounterLoot" role="status">
        {killed ? (
          <>
            <strong>
              {training ? 'YOUR FIRST CACHE / RECOVERED' : 'LOOT RECOVERED'}
            </strong>
            {result.guardian && (
              <p>
                Defeated in {result.attacks} attack
                {result.attacks === 1 ? '' : 's'}
                {result.miniBoss
                  ? ' · Guaranteed Warden relic'
                  : result.attacks === 1
                  ? ' · First-strike bonus +1 shard'
                  : ''}
              </p>
            )}
            <div className="coreboundLootDrops">
              {loot.shards > 0 && (
                <div className="coreboundLootDrop is-shards">
                  <b aria-hidden="true">✦</b>
                  <span>+{loot.shards} shards</span>
                </div>
              )}
              {loot.heal > 0 && (
                <div className="coreboundLootDrop is-heal">
                  <RewardPotion />
                  <span>+{loot.heal} HP · repair vial</span>
                </div>
              )}
              {loot.relic && (
                <div className="coreboundLootDrop is-relic">
                  <RelicIcon
                    relicId={loot.relic}
                    icon={RELICS[loot.relic]?.icon}
                  />
                  <span>{RELICS[loot.relic]?.name || 'Relic recovered'}</span>
                </div>
              )}
              {loot.relicChoice && (
                <div className="coreboundLootDrop is-relic">
                  <b aria-hidden="true">◈</b>
                  <span>Choose a relic from the Warden’s cache</span>
                </div>
              )}
              {!loot.shards &&
                !loot.heal &&
                !loot.relic &&
                !loot.relicChoice && (
                  <span className="coreboundLootRoute">
                    Portal route stabilized
                  </span>
                )}
            </div>
            {loot.heal > 0 && (
              <small>Healing overflow grows your maximum HP.</small>
            )}
          </>
        ) : (
          <>
            <strong>{remaining} ENEMY HP REMAINING</strong>
            <p>
              {result.miniBoss
                ? 'Its health stays reduced for your next throw.'
                : 'Encounter loot lost. Your landing tile rewards stay with you.'}
            </p>
            <span>
              {result.damage > 0
                ? 'Retaliation: −' + result.damage + ' HP'
                : 'Retaliation blocked'}
            </span>
          </>
        )}
      </div>
      <RewardCollection
        result={result}
        originRef={rewardOriginRef}
        healthRef={healthRef}
        shardsRef={shardsRef}
        delay={reduceMotion ? 0 : 1150}
        reduceMotion={reduceMotion}
        onProgress={onRewardProgress}
      />
      <button type="button" className="coreboundPrimary" onClick={finish}>
        {training
          ? 'Enter the portal network →'
          : reduceMotion
          ? 'Continue →'
          : 'Continue / skip animation →'}
      </button>
    </div>,
    document.body
  );
};

export default EncounterLootSequence;
